import { Avatar, Badge, Divider } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { textStyleCss } from 'src/theme/atlas';
import { NavbarVariantEnum } from 'src/types';
import type { DefaultTheme } from 'styled-components';
import styled, { css } from 'styled-components';

export const NAVBAR_TRANSITION = '0.6s cubic-bezier(0.22, 1, 0.36, 1)';

const TRANSITION_DURATION = '0.36s';

export const OPEN_WIDTH = '260px';
const CLOSED_WIDTH = '64px';

const getVariantStyles = (theme: DefaultTheme) => ({
  reporter: {
    borderRight: theme.isVaultVariant
      ? `1px solid ${theme.newPalette.neutral.N400}`
      : `1px solid ${theme.atlas.semantic.color.outline.fixed}`,
    dividerColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N400
      : theme.atlas.semantic.color.ui.divider.default,
    backgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N100
      : theme.atlas.semantic.color.surface.subtle,
    backgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N200
      : theme.atlas.semantic.color.surface.variant,
    bottomBackgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N100
      : theme.atlas.semantic.color.surface.subtle,
    bottomBackgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N200
      : theme.atlas.semantic.color.surface.variant,
    textColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N900
      : theme.atlas.semantic.color.type.default,
  },
  manager: {
    borderRight: 'none',
    dividerColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N700
      : theme.atlas.semantic.color.ui.divider.secondary,
    backgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N800
      : theme.atlas.semantic.color.surface.inverse,
    backgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N700
      : theme.atlas.semantic.color.action.secondaryInverse.hover,
    bottomBackgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N900
      : theme.atlas.semantic.color.surface.inverse,
    bottomBackgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N800
      : theme.atlas.semantic.color.action.secondaryInverse.hover,
    textColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N0
      : theme.atlas.semantic.color.action.secondaryInverse.onSecondaryInverse,
  },
  public: {
    borderRight: theme.isVaultVariant
      ? `1px solid ${theme.newPalette.neutral.N400}`
      : `1px solid ${theme.atlas.semantic.color.outline.fixed}`,
    dividerColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N400
      : theme.atlas.semantic.color.ui.divider.default,
    backgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N0
      : theme.atlas.semantic.color.surface.default,
    backgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N100
      : theme.atlas.semantic.color.surface.subtle,
    bottomBackgroundColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N0
      : theme.atlas.semantic.color.surface.default,
    bottomBackgroundHoverColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N100
      : theme.atlas.semantic.color.surface.subtle,
    textColor: theme.isVaultVariant
      ? theme.newPalette.neutral.N800
      : theme.atlas.semantic.color.type.default,
  },
});

type NavbarStyledProps = {
  open: boolean;
  variant: NavbarVariantEnum;
  responsive: boolean;
  secondaryDrawer?: boolean;
};

export const NavbarStyled = styled.div<NavbarStyledProps>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: sticky;
  width: ${(props) => (props.open ? OPEN_WIDTH : CLOSED_WIDTH)};
  height: 100vh;
  left: 0;
  z-index: 2000;
  background-color: ${(props) =>
    getVariantStyles(props.theme)[props.variant].backgroundColor};
  border-right: ${(props) =>
    getVariantStyles(props.theme)[props.variant].borderRight};
  transition: width ${NAVBAR_TRANSITION};

  ${(props) =>
    props.responsive &&
    css<NavbarStyledProps>`
      @media (max-width: ${(props) => props.theme.newBreakpoints.narrow}px) {
        margin-top: ${(props) =>
          props.variant === NavbarVariantEnum.PUBLIC ? '0' : '-60px'};
        ${(props) =>
          props.open
            ? css`
                display: flex;
                width: 100%;
              `
            : css`
                width: ${props.secondaryDrawer ? '100%' : '0'};
              `};
      }
    `}
`;

export const ReporterNavbarBottomMenu = styled(NavbarStyled)`
  height: auto;

  @media (max-width: ${(props) => props.theme.newBreakpoints.narrow}px) {
    ${(props) =>
      props.open
        ? css`
            display: flex;
            width: 100%;
          `
        : css`
            display: none;
          `};
  }
`;

export const PublicNavbarBottomMenu = styled.div<{
  open: boolean;
  variant: NavbarVariantEnum;
  responsive?: boolean;
}>`
  /* Mobile responsive styles for Public navbar */
  @media (max-width: ${(props) => props.theme.newBreakpoints.narrow}px) {
    ${(props) =>
      props.open
        ? css`
            /* When open on mobile, show all items */
            ${AccountNavLink} {
              display: flex;
            }
            ${StyledNavBarIcon} {
              display: block;
            }
            ${ProfileContainer} {
              display: flex;
            }
            > div {
              display: block;
            }
          `
        : css`
            /* When closed on mobile, hide all bottom menu items */
            ${AccountNavLink} {
              display: none;
            }
            ${ProfileContainer} {
              display: none;
            }
            > div {
              display: none;
            }
          `};
  }
`;

export const DividerContainer = styled.div`
  flex-grow: 2;
`;

export const NavbarDivider = styled(Divider).attrs<{
  navbarVariant: NavbarVariantEnum;
}>((props) => ({
  color: getVariantStyles(props.theme)[props.navbarVariant].dividerColor,
}))<{ navbarVariant: NavbarVariantEnum }>``;

export const Top = styled.div<{ open: boolean }>`
  margin: 20px;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.open ? 'space-between' : 'center')};
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StyledHeader = styled.h1`
  margin: 0;
  padding: 0;
  white-space: nowrap;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.action.secondaryInverse
          .onSecondaryInverse};
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleDisplayMedium
      : textStyleCss(theme.atlas.typography.semantic.headingH3)};
  font-size: 18px;
`;

export const ToggleNavbar = styled.div<{
  open: boolean;
  variant?: NavbarVariantEnum;
}>`
  cursor: pointer;
  z-index: 2001;
  display: flex;
  align-items: center;

  /* Vault mode: add the dark rounded-rectangle background box around the icon */
  ${(props) =>
    props.theme.isVaultVariant &&
    props.variant === NavbarVariantEnum.MANAGER &&
    `
    background-color: ${props.theme.atlas.core.color.gray[950]};
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
  `}

  /* Styles for non-Reporter variants (Public, Manager) that use img */
  img {
    transform: ${(props) => (props.open ? '' : 'rotate(180deg)')};
    transition: transform ${NAVBAR_TRANSITION};
  }

  /* Styles for Reporter/Public variants that use StyledIcon */
  ${StyledIcon} {
    transition: transform ${NAVBAR_TRANSITION};
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.type.default};
  }
`;

export const Nav = styled.nav`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 8px;
  margin: 0;
  list-style: none;
  flex-grow: 2;
  padding-bottom: 60px;
  &::-webkit-scrollbar {
    height: 7px;
    width: 8px;
  }

  &::-webkit-scrollbar,
  &::-webkit-scrollbar-corner {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N800
        : props.theme.atlas.semantic.color.surface.inverse};
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N700
        : props.theme.atlas.semantic.color.ui.scrollbar.handle};
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N700
        : props.theme.atlas.semantic.color.ui.scrollbar.handle} !important;
    border-radius: 5px;
  }
`;

export const NavList = styled.ul`
  list-style-type: none;
  width: 100%;
  margin: 0px;
  padding-left: 0px;
  padding-right: 0px;
`;

export const NavHeader = styled.p<{
  navOpen: boolean;
  variant: NavbarVariantEnum;
}>`
  padding-left: 20px;
  display: ${(props) => (props.navOpen ? 'auto' : 'none')};
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleSubheading
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) => getVariantStyles(props.theme)[props.variant].textColor};
  overflow: hidden;
  opacity: ${(props) => (props.navOpen ? '1' : '0')};
  width: ${(props) => (props.navOpen ? '100%' : '0%')};
  transition: all ${TRANSITION_DURATION};
  padding-left: 26px;
  font-weight: 600;
`;

export const BottomContainer = styled.div<{ variant: NavbarVariantEnum }>`
  position: relative;
  flex-shrink: 0;
  background-color: ${(props) =>
    getVariantStyles(props.theme)[props.variant].bottomBackgroundColor};
`;

export const Bottom = styled.div<{ variant: NavbarVariantEnum }>`
  &:before {
    pointer-events: none;
    content: ' ';
    left: 0px;
    top: -54px;
    position: absolute;
    height: 54px;
    width: 100%;
    z-index: -1;
    ${(props) =>
      props.variant === 'manager' &&
      css`
        background: linear-gradient(
          180deg,
          ${(props) =>
              props.theme.isVaultVariant
                ? props.theme.newPalette.neutral.N800
                : props.theme.atlas.semantic.color.surface.inverse}00
            30%,
          ${(props) =>
              props.theme.isVaultVariant
                ? props.theme.newPalette.neutral.N900
                : props.theme.atlas.semantic.color.surface.inverse}
            100%
        );
        background-repeat: no-repeat;
      `}
  }
  padding-top: 20px;
  padding-bottom: 20px;
`;

export const ProfileContainer = styled.div<{
  settingsTooltip?: boolean;
  variant: NavbarVariantEnum;
}>`
  display: flex;
  cursor: pointer;
  padding: 10px 20px 10px 10px;
  width: full;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  background-color: ${(props) =>
    props.settingsTooltip
      ? getVariantStyles(props.theme)[props.variant].backgroundColor
      : ''};
  color: ${(props) => getVariantStyles(props.theme)[props.variant].textColor};
  &:hover {
    background-color: ${(props) =>
      getVariantStyles(props.theme)[props.variant].bottomBackgroundHoverColor};
  }
`;
export const AccountTextContainer = styled.div<{ open?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: between;
  align-items: center;
  width: ${(props) => (props.open ? '100%' : '0%')};
  transition: width ${TRANSITION_DURATION};
`;
export const AccountTitle = styled.h2<{
  open: boolean;
  variant: NavbarVariantEnum;
}>`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleSubheading
      : textStyleCss(theme.atlas.typography.semantic.caption)}
  overflow: hidden;
  opacity: ${(props) => (props.open ? '1' : '0')};
  width: ${(props) => (props.open ? '100%' : '0%')};
  transition: width ${TRANSITION_DURATION};
  transition: opacity ${TRANSITION_DURATION};
  padding-left: 20px;
  color: ${(props) => getVariantStyles(props.theme)[props.variant].textColor};
`;

export const AccountText = styled.span<{
  open: boolean;
}>`
  justify-content: start;
  overflow: hidden;
  opacity: ${(props) => (props.open ? '1' : '0')};
  width: ${(props) => (props.open ? '100%' : '0%')};
  margin: 4px;
  overflow: hidden;
  white-space: nowrap;
  transition: width ${TRANSITION_DURATION};
  transition: opacity ${TRANSITION_DURATION};
  flex-grow: 2;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  padding-left: 10px;
`;

export const AccountNavLink = styled.div<{
  notificationOpen?: boolean;
  variant: NavbarVariantEnum;
}>`
  display: flex;
  cursor: pointer;
  padding: 12px 16px;
  position: relative;
  box-sizing: border-box;
  text-align: left;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) =>
    props.notificationOpen || props.variant === 'reporter'
      ? props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N900
        : props.theme.atlas.semantic.color.type.default
      : getVariantStyles(props.theme)[props.variant].textColor};
  background-color: ${(props) =>
    props.notificationOpen
      ? props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.surface.default
      : getVariantStyles(props.theme)[props.variant].bottomBackgroundColor};
  &:hover {
    color: ${(props) => getVariantStyles(props.theme)[props.variant].textColor};
    background-color: ${(props) =>
      getVariantStyles(props.theme)[props.variant].bottomBackgroundHoverColor};
  }
  &:active {
    background-color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.surface.default};
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N800
        : props.theme.atlas.semantic.color.type.default};
  }
`;

export const LinkWithBadgeContainer = styled.div<{
  open: boolean;
  notification?: boolean;
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
export const StyledNotificationBadge = styled(Badge)<{ open: boolean }>`
  opacity: ${(props) => (props.open ? '1' : '0')};
  display: ${(props) => (props.open ? 'block' : 'none')};
  transition: all 0.2s;
`;
export const NavBarIconNotificationContainer = styled.div<{
  open: boolean;
  notification: boolean;
}>`
  display: flex;
  position: ${(props) => (!props.open && props.notification ? 'absolute' : '')};
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const StyledNavBarIcon = styled.i<{
  color?: string;
  size?: string;
}>`
  font-size: ${(props) => props.size};
  color: ${(props) => props.color};
  margin-right: 8px;
  margin-left: 6px;
`;
export const StyledNavAvatar = styled(Avatar)`
  margin-left: 6px;
`;

export const MobileBurgerContainer = styled.div<{
  open: boolean;
  variant: NavbarVariantEnum;
}>`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  height: 60px;
  background-color: ${(props) => {
    if (props.variant === NavbarVariantEnum.REPORTER) {
      return props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N200
        : props.theme.atlas.semantic.color.surface.variant;
    }
    if (props.variant === NavbarVariantEnum.PUBLIC) {
      return props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.surface.default;
    }
    return '';
  }};
  padding-top: 20px;
  padding-left: 20px;

  display: none;

  @media (max-width: ${(props) => props.theme.newBreakpoints.narrow}px) {
    display: block;
  }
`;

export const MobileBurger = styled(StyledIcon).attrs({
  tabIndex: 0,
  role: 'button',
  'aria-label': 'Toggle navigation',
})`
  @media (max-width: ${(props) => props.theme.newBreakpoints.narrow}px) {
    cursor: pointer;
  }
`;
